import React from 'react'
import {
  withApolloSSR,
  withApolloSSRProvider,
  WithApolloSSRProps,
} from 'hoc/with-apollo-ssr'
import withCurrentUser from 'middleware/with-current-user'
import contentfulClient from 'clients/contentful'
import { PlusJakartaSansProvider } from 'providers/font-providers/plus-jakarta-sans-provider'
import Layout from 'components/layout'
import CurrentUserProvider from 'providers/current-user-provider'
import { parseSurvey } from 'utilities/survey-parser'
import { getClientResponses } from 'components/shared/cms-survey/queries.graphql'
import QuestionPage from 'components/survey/question-page'
import { submitClientSignUpSurvey } from 'components/sign-up/mutations.graphql'
import { SESSION_TOKEN, PAYMENT_TOKEN } from 'constants/cookies'
import { getFirstStringFromOptionalArray } from 'utilities/strings'
import { isBot } from 'utilities/user-agent'
import { destroyCookie, parseCookies } from 'nookies'
import { setSignInCookie } from 'utilities/cookies'
import { parseStoredClickIds } from 'utilities/tracking-consent'
import PusherProvider from 'providers/pusher-provider'
import ConversionTracking from 'components/shared/conversion-tracking'
import { getFirstUnansweredQuestionId } from 'utilities/client-helpers'
import bugsnagClient from 'clients/bugsnag'
import { FULL_HEIGHT_DVH_STYLES } from 'constants/styles'
import {
  LP_VARIANTS,
  applyLpVariant,
  handoffModelPathResponses,
  lpVariantFrom,
} from 'components/survey/lp-variants'
import {
  SIGN_UP_HANDOFF_COOKIE,
  decodeSignUpHandoff,
  withoutAnsweredHandoff,
  type SignUpHandoff,
} from 'utilities/sign-up-handoff'
import { saveSignUpHandoff, savedHandoff } from 'utilities/save-sign-up-handoff'
import { STATE_RESPONSES } from 'components/survey/question-types/custom/insurance-covered-loading'

const SURVEY_ID = 48 // Insurance clients sign-up survey

interface SignUpPageProps extends WithApolloSSRProps {
  survey: {
    id: number
    name: string
    sections: Array<{
      id: number
      name: string
      heading?: string
      questions: Array<{
        id: number
        text: string
        type: string
        isRequired?: boolean
        maxSelections?: number
        responses?: Array<{
          id: number
          text: string
          nestedQuestions?: any[]
        }>
      }>
    }>
  }
  entryId: string
  user?: any
  clientId?: number
  coachId?: number
  leadId?: number
  currentQuestionId?: number | string
  existingResponses?: Array<{
    id: number
    surveyQuestionId: number
    surveyQuestionResponseId?: number
    responseValue?: string
  }>
  leadInfo?: {
    landingPageUrl?: string
    deviceType?: string
    utmCampaignId?: string
    utmCampaign?: string
    utmSource?: string
    utmMedium?: string
    utmTerm?: string
    utmContent?: string
    referrer?: string
    userAgent?: string
  }
  isImpersonation?: boolean
}

const SignUpPage = ({
  survey,
  entryId,
  user,
  clientId,
  coachId,
  leadId,
  currentQuestionId,
  existingResponses = [],
  leadInfo,
  isImpersonation,
}: SignUpPageProps) => {
  if (!survey) {
    return (
      <PlusJakartaSansProvider>
        <Layout noIndex={true} title="Survey Not Found - Kickoff">
          <CurrentUserProvider shouldIdentifyUser={false}>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <h1>Survey Not Found</h1>
              <p>The survey could not be found.</p>
            </div>
          </CurrentUserProvider>
        </Layout>
      </PlusJakartaSansProvider>
    )
  }

  // Determine which question to show
  // If currentQuestionId prop exists, use that; otherwise use first question
  const allQuestions = survey.sections.flatMap(section => section.questions)
  const questionIdToShow =
    currentQuestionId || (allQuestions.length > 0 ? allQuestions[0].id : null)

  if (!questionIdToShow) {
    return (
      <PlusJakartaSansProvider>
        <Layout noIndex={true} title="Survey Error - Kickoff">
          <CurrentUserProvider shouldIdentifyUser={false}>
            <PusherProvider>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <h1>Survey Error</h1>
                <p>This survey has no questions.</p>
              </div>
            </PusherProvider>
          </CurrentUserProvider>
        </Layout>
      </PlusJakartaSansProvider>
    )
  }

  return (
    <PlusJakartaSansProvider>
      <Layout noIndex={true} title={`Sign Up - Kickoff`} metaTags={[]}>
        <CurrentUserProvider shouldIdentifyUser={true} forceNetworkFetch={true}>
          <PusherProvider>
            <QuestionPage
              survey={survey}
              entryId={entryId}
              currentQuestionId={questionIdToShow}
              clientId={clientId}
              user={user}
              coachId={coachId}
              leadId={leadId}
              existingResponses={existingResponses}
              leadInfo={leadInfo}
              isImpersonation={isImpersonation}
            />
          </PusherProvider>
        </CurrentUserProvider>
      </Layout>
      {FULL_HEIGHT_DVH_STYLES}
      <style jsx global>{`
        body {
          background-color: #edf4fb;
          overflow: hidden;
        }
        body * {
          box-sizing: border-box;
        }
      `}</style>
      {/* Conversion tracking for sign-up funnel */}
      {/* its being used in the layout component so we don't need to render it here */}
      <ConversionTracking enabled={false} />
    </PlusJakartaSansProvider>
  )
}

export const getServerSideProps = withApolloSSR()(
  withCurrentUser(async ctx => {
    const { query, apolloClient, req } = ctx
    const { questionId } = query
    // An inline landing page's variant (see components/survey/lp-variants).
    const lpVariant = lpVariantFrom(query.lp)

    let clientId: number | undefined
    let coachId: number | undefined
    let leadId: number | undefined
    let leadInfo: SignUpPageProps['leadInfo'] | undefined
    let newMe: any = null
    let createdClientThisRequest = false

    // Get client ID from current user
    const currentUserPayload = ctx.currentUserPayload
    const me = currentUserPayload?.data?.me
    const isImpersonation = ctx.impersonation?.isImpersonation || false

    // Check if there's an existing user who is not an insurance client
    // If so, clear the session to allow creating a new insurance client
    if (
      currentUserPayload &&
      me?.user?.client &&
      !me?.user?.client?.isInsuranceClient &&
      !isImpersonation &&
      !isBot(req?.headers['user-agent'])
    ) {
      // Clear the session by destroying cookies
      await destroyCookie(ctx, SESSION_TOKEN, { path: '/' })
      await destroyCookie(ctx, PAYMENT_TOKEN, { path: '/' })

      // Clear the currentUserPayload to force a new client creation
      // Reset clientId and related variables
      clientId = undefined
      coachId = undefined
      leadId = undefined
    }

    if (me?.user?.client?.isInsuranceClient) {
      clientId = parseInt(me.user.client.id, 10)
      if (me.user.client.coach) {
        coachId = parseInt(me.user.client.coach.id, 10)
      }
      // Get leadId from existing client if available
      if (me.user.client.lead?.id) {
        leadId = parseInt(me.user.client.lead.id, 10)
      }
    }

    // Create client if it doesn't exist (for insurance clients sign-up)
    // Skip if user is a bot or if impersonating
    if (!clientId && !isImpersonation && !isBot(req?.headers['user-agent'])) {
      try {
        // Click IDs and UTMs are captured to a first-party cookie on landing;
        // fall back to it when the params dropped off the URL along the way,
        // so server-side CAPI + UTM attribution survives navigation.
        const storedClickIds = parseStoredClickIds(parseCookies({ req }))
        const utmCampaignId =
          getFirstStringFromOptionalArray(query.utm_campaign_id) ||
          storedClickIds.utm_campaign_id
        const utmCampaign =
          getFirstStringFromOptionalArray(query.utm_campaign) ||
          storedClickIds.utm_campaign
        const utmContent =
          getFirstStringFromOptionalArray(query.utm_content) ||
          storedClickIds.utm_content
        const utmMedium =
          getFirstStringFromOptionalArray(query.utm_medium) ||
          storedClickIds.utm_medium
        const utmSource =
          getFirstStringFromOptionalArray(query.utm_source) ||
          storedClickIds.utm_source
        const utmTerm =
          getFirstStringFromOptionalArray(query.utm_term) ||
          storedClickIds.utm_term
        const ref = getFirstStringFromOptionalArray(
          query.ref || query.refwh || query.slug
        )
        const gclid =
          getFirstStringFromOptionalArray(query.gclid) || storedClickIds.gclid
        const msclkid =
          getFirstStringFromOptionalArray(query.msclkid) ||
          storedClickIds.msclkid
        const fbclid =
          getFirstStringFromOptionalArray(query.fbclid) || storedClickIds.fbclid
        const ttclid =
          getFirstStringFromOptionalArray(query.ttclid) || storedClickIds.ttclid
        const couponCode = getFirstStringFromOptionalArray(query.couponCode)

        // timeZone/utcOffset are intentionally omitted here — getServerSideProps
        // can't read the visitor's tz, and writing the server's offset would be
        // misleading. The question-page effect captures both on hydration.
        // User.$beforeInsert defaults utcOffset to -5 when missing.

        // Extract referrer and device info from headers
        const referrer =
          req?.headers.referer || req?.headers.referrer || undefined
        const userAgent = req?.headers['user-agent'] || ''
        const deviceType = /Mobile|Android|iPhone|iPad/.test(userAgent)
          ? 'mobile'
          : 'desktop'
        const landingPageUrl = req?.url || undefined

        const { data: signUpData } = await apolloClient.mutate({
          mutation: submitClientSignUpSurvey,
          variables: {
            ref,
            gclid,
            msclkid,
            fbclid,
            ttclid,
            couponCode,
            responses: [], // Empty responses initially - answers will be submitted incrementally
            ...(lpVariant && {
              signUpVariant: LP_VARIANTS[lpVariant].signUpVariant,
            }),
            isInsuranceClient: true, // Survey ID 48 is for insurance
            // clients
            utmCampaignId,
            utmCampaign,
            utmContent,
            utmMedium,
            utmSource,
            utmTerm,
            landingPageUrl,
            deviceType,
            referrer,
            userAgent,
          },
        })

        const {
          sessionToken,
          paymentToken,
          me: mutationNewMe,
          leadId: newLeadId,
        } = signUpData.submitClientSignUpSurvey

        newMe = mutationNewMe

        // Extract clientId and leadId from the response
        if (newMe?.user?.client?.id) {
          clientId = parseInt(newMe.user.client.id, 10)
          createdClientThisRequest = true
        }

        // Store leadId and leadInfo for passing to component
        if (newLeadId) {
          leadId = parseInt(String(newLeadId), 10)
        }

        // Prepare leadInfo from the data we already have
        // Filter out undefined values to avoid serialization errors
        leadInfo = Object.fromEntries(
          Object.entries({
            landingPageUrl,
            deviceType,
            utmCampaignId,
            utmCampaign,
            utmSource,
            utmMedium,
            utmTerm,
            utmContent,
            referrer,
            userAgent,
          }).filter(([_, value]) => value !== undefined)
        )

        // Set cookies server-side for authentication
        if (sessionToken) {
          await destroyCookie(ctx, SESSION_TOKEN, { path: '/' })
          await setSignInCookie(ctx, SESSION_TOKEN, sessionToken)
          // hack for auth in SSR
          ctx.overrideCookies = { [SESSION_TOKEN]: sessionToken }

          // Update Apollo client context reference to pick up new sessionToken
          if ((apolloClient as any).__contextRef) {
            ;(apolloClient as any).__contextRef.current = ctx
          }
        }

        if (paymentToken) {
          await destroyCookie(ctx, PAYMENT_TOKEN, { path: '/' })
          await setSignInCookie(ctx, PAYMENT_TOKEN, paymentToken)
          // Also set in overrideCookies for SSR
          ctx.overrideCookies = {
            ...ctx.overrideCookies,
            [PAYMENT_TOKEN]: paymentToken,
          }

          // Update Apollo client context reference to pick up new paymentToken
          if ((apolloClient as any).__contextRef) {
            ;(apolloClient as any).__contextRef.current = ctx
          }
        }
      } catch (error) {
        // Log error but continue - allow survey to proceed even if client creation fails
        // The user can still answer questions, but responses won't be saved until clientId exists
        const userAgent = req?.headers['user-agent'] || ''
        const referrer =
          req?.headers.referer || req?.headers.referrer || undefined
        const deviceType = /Mobile|Android|iPhone|iPad/.test(userAgent)
          ? 'mobile'
          : 'desktop'
        const landingPageUrl = req?.url || undefined

        bugsnagClient.notify(error, bugsnagEvent => {
          bugsnagEvent.addMetadata('clientCreation', {
            surveyId: SURVEY_ID,
            mutation: 'submitClientSignUpSurvey',
            queryParams: {
              ref: getFirstStringFromOptionalArray(
                query.ref || query.refwh || query.slug
              ),
              gclid: getFirstStringFromOptionalArray(query.gclid),
              msclkid: getFirstStringFromOptionalArray(query.msclkid),
              fbclid: getFirstStringFromOptionalArray(query.fbclid),
              couponCode: getFirstStringFromOptionalArray(query.couponCode),
              utmCampaignId: getFirstStringFromOptionalArray(
                query.utmCampaignId
              ),
              utmCampaign: getFirstStringFromOptionalArray(query.utmCampaign),
              utmContent: getFirstStringFromOptionalArray(query.utmContent),
              utmMedium: getFirstStringFromOptionalArray(query.utmMedium),
              utmSource: getFirstStringFromOptionalArray(query.utmSource),
              utmTerm: getFirstStringFromOptionalArray(query.utmTerm),
            },
            isInsuranceClient: true,
          })
          bugsnagEvent.addMetadata('request', {
            userAgent,
            referrer,
            deviceType,
            landingPageUrl,
            isImpersonation,
            hasCurrentUser: !!me,
            userId: me?.user?.id,
            email: me?.user?.email,
            method: req?.method,
            url: req?.url,
          })
          bugsnagEvent.severity = 'error'
        })
        // Don't throw - allow the page to render
      }
    }

    // Save what an inline landing page handed over (pages/api/sign-up/start),
    // now that the visitor has a sign-up client, then drop the cookie.
    let handedOff: SignUpHandoff | null = null
    const handoffCookie = parseCookies({ req })[SIGN_UP_HANDOFF_COOKIE]
    if (handoffCookie) {
      const handoff = decodeSignUpHandoff(handoffCookie)
      if (
        lpVariant &&
        clientId &&
        !isImpersonation &&
        handoff?.variant === lpVariant
      ) {
        // A sign-up that already exists keeps every answer it has.
        let toSave = handoff
        if (!createdClientThisRequest) {
          const responses = await apolloClient
            .query({
              query: getClientResponses,
              variables: {
                clientId: String(clientId),
                surveyId: String(SURVEY_ID),
              },
              // Stay out of the request's cache: the read below must see the
              // answers saved next. An SSR client turns network-only into
              // cache-first, so that read can't refuse a cached list.
              fetchPolicy: 'no-cache',
            })
            .then(result => result.data?.client?.cmsSurveyQuestionResponses)
            .catch(() => null)
          toSave = responses
            ? withoutAnsweredHandoff(handoff, me?.user || null, responses)
            : { variant: handoff.variant }
        }
        const savedQuestionIds = await saveSignUpHandoff(
          apolloClient,
          clientId,
          toSave,
          STATE_RESPONSES,
          (error, surveyQuestionId) =>
            bugsnagClient.notify(
              new Error('Sign-up hand-off answer not saved'),
              event => {
                event.addMetadata('signUpHandoff', {
                  clientId,
                  surveyQuestionId,
                  variant: lpVariant,
                  errorName: (error as Error)?.name,
                })
              }
            )
        )
        handedOff = savedHandoff(toSave, savedQuestionIds)
      }
      await destroyCookie(ctx, SIGN_UP_HANDOFF_COOKIE, { path: '/sign-up' })
    }

    // Fetch survey from Contentful
    if (!contentfulClient) {
      console.error('Contentful client not available')
      return {
        notFound: true,
      }
    }

    let surveyResponse
    try {
      surveyResponse = await contentfulClient.getEntries({
        content_type: 'survey',
        'fields.id': SURVEY_ID,
        include: 4, // Include linked entries
      })

      if (!surveyResponse.items || surveyResponse.items.length === 0) {
        console.error('Survey not found')
        return {
          notFound: true,
        }
      }
    } catch (error) {
      console.error('Error fetching survey:', error)
      return {
        notFound: true,
      }
    }

    const entryId = surveyResponse.items[0].sys.id
    const parsedSurvey = parseSurvey(surveyResponse.items[0])
    const survey = lpVariant
      ? applyLpVariant(parsedSurvey, lpVariant)
      : parsedSurvey

    // Fetch existing responses if clientId is available
    let existingResponses: any[] = []
    if (clientId) {
      try {
        const responsesResult = await apolloClient.query({
          query: getClientResponses,
          variables: {
            clientId: String(clientId),
            surveyId: String(SURVEY_ID),
          },
        })
        existingResponses =
          responsesResult.data?.client?.cmsSurveyQuestionResponses || []
      } catch (error) {
        console.error('Error fetching existing responses:', error)
        // Continue without existing responses
      }
    }

    // Name and date of birth live on the user record, not in survey
    // responses, so rebuild the handed-over ones for the variant flow.
    if (lpVariant && clientId) {
      // A client created this request belongs to newMe; the earlier session
      // (a non-insurance user, cleared above) must not fill its answers.
      const user = createdClientThisRequest
        ? newMe?.user || null
        : currentUserPayload?.data?.me?.user || newMe?.user || null
      existingResponses = [
        ...existingResponses,
        ...handoffModelPathResponses(
          lpVariant,
          {
            firstName: handedOff?.firstName ?? user?.firstName,
            lastName: handedOff?.lastName ?? user?.lastName,
            birthDate: handedOff?.dateOfBirth ?? user?.birthDate,
          },
          existingResponses
        ),
      ]
    }

    // Determine which question to show
    // Priority:
    // 1. Explicit questionId query param (if provided)
    // 2. First unanswered question (using helper function)
    // 3. First question in survey (fallback)
    const allQuestions = survey.sections.flatMap(section => section.questions)

    let currentQuestionId: number | string | null = null

    // Priority 1: Use explicit questionId from query if provided
    if (questionId && typeof questionId === 'string') {
      currentQuestionId = questionId
    } else {
      // Priority 2: Find first unanswered question
      const firstUnansweredId = getFirstUnansweredQuestionId({
        questions: allQuestions,
        responses: existingResponses,
      })

      // Priority 3: Fallback to first question if all are answered
      currentQuestionId =
        firstUnansweredId ||
        (allQuestions.length > 0 ? allQuestions[0].id : null)
    }

    return {
      props: {
        survey: survey,
        entryId,
        clientId: clientId || null,
        user: currentUserPayload?.data?.me?.user || newMe?.user || null,
        coachId: coachId || null,
        leadId: leadId || null,
        currentQuestionId: currentQuestionId || null,
        existingResponses,
        leadInfo: leadInfo || null,
        isImpersonation,
      },
    }
  })
)

export default withApolloSSRProvider(SignUpPage)
